/**
 * "Continue your case": every link into the Build your case funnel, for a visitor who left a case
 * or a part-answered quiz behind, says so and takes them straight back to it. Until 2026-10-05 the
 * label was "Add to a mixed case", which read wrong on the collection tile, menus and banners, where
 * nothing is being added; Matt's call that day: those say "Build your case" to a new visitor and
 * "Continue your case" to a returning one.
 *
 * WHICH VISITOR. One with a fresh saved case, or a fresh saved quiz holding at least one answer (a
 * skipped question is an answer: the quiz saves it as ''). Freshness, the key names and the
 * fourteen days are assets/club-case-store.js's, imported, never restated. Anyone else, and anyone
 * whose storage throws, gets the page exactly as it was rendered: nothing here writes until the
 * decision says pick up.
 *
 * WHICH LINKS. Two kinds, because the theme writes one and the merchant the other.
 *   - Everything snippets/club-build-url.liquid writes carries `data-club-entry`. Its words sit in
 *     a child marked `data-club-entry-label`, which becomes the translated "Continue your case". A
 *     link without that child keeps its words on purpose (the wine card's "In the club" line is a
 *     fact about the wine, not an invitation) and is left alone entirely. A link marked
 *     `data-club-entry-fixed` keeps its words but still gains the resume: the product page's
 *     "Add to a mixed case" button, where the words mean adding this wine.
 *   - Menu items are store data and carry nothing. A link whose path is the builder's gains the
 *     resume too, and its words change only when they are exactly the builder label ("Build your
 *     case"). Any other menu wording is the merchant's, and stays.
 * Both keep their `entry`: `resume=1` is joined on, never swapped in.
 *
 * THE STRINGS come from Liquid, on this file's own script tag in layout/theme.liquid: the
 * translated label, the builder label a menu item is compared with, and the builder's address. No
 * English lives here.
 *
 * CONTENT THAT ARRIVES LATER. The cart drawer morphs its summary on every change (the cart prompt
 * lives there) and the collection grid on every filter and sort (the club tile). assets/morph.js
 * patches in place where it can: it sets an `href` back, or a label's text node back, rather than
 * adding a node. So one MutationObserver on the body watches added nodes, `href` and text, keeps
 * only records that touch a builder link, and puts the pick-up back in the same microtask, before
 * the browser paints. The rewrite is idempotent, so its own mutations come back as a pass that
 * changes nothing and the loop ends there.
 *
 * NODE. Everything the decision and the rewrite rest on is exported and takes its inputs as
 * arguments, so scripts/tests/club-pickup.test.mjs runs it without a browser. The store arrives by
 * dynamic import only in a page, through the import map, so its versioned URL is the one used (a
 * relative import would fetch an unversioned URL the CDN caches for a year).
 */

/** The label child snippets/club-build-url.liquid asks callers to mark. */
const LABEL = '[data-club-entry-label]';
const ENTRY = '[data-club-entry]';
/** A builder link whose words are an action on this page's wine, not an invitation: kept. */
const FIXED = 'data-club-entry-fixed';

/**
 * True when the saved quiz is fresh and holds at least one answer.
 * @param {string | null} raw the stored JSON
 * @param {(savedAt: unknown, now?: number) => boolean} isFresh
 * @param {number} [now]
 */
export function quizUnderway(raw, isFresh, now = Date.now()) {
  if (!raw) return false;
  try {
    const saved = JSON.parse(raw);
    const answers = saved?.answers;
    if (!answers || typeof answers !== 'object' || !isFresh(saved.savedAt, now)) return false;
    return Object.values(answers).some((value) => typeof value === 'string');
  } catch {
    return false;
  }
}

/**
 * The one decision: pick up, or leave the page alone.
 * @param {{ readCase: Function, isFresh: Function, QUIZ_KEY: string }} store assets/club-case-store.js
 * @param {{ storage?: Storage | null, now?: number }} [options]
 */
export function shouldPickUp(store, { storage, now = Date.now() } = {}) {
  try {
    const local = storage === undefined ? globalThis.localStorage : storage;
    if (!local) return false;
    if (store.readCase({ storage: local, now })) return true;
    return quizUnderway(local.getItem(store.QUIZ_KEY), store.isFresh, now);
  } catch {
    return false;
  }
}

/**
 * `href` with `resume=1` joined onto its query, before any hash; `entry` and the rest untouched.
 * Returned as it came when it already carries `resume=1`.
 * @param {string} href
 */
export function withResume(href) {
  const hashAt = href.indexOf('#');
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  const base = hashAt === -1 ? href : href.slice(0, hashAt);
  const queryAt = base.indexOf('?');
  const path = queryAt === -1 ? base : base.slice(0, queryAt);
  const params = queryAt === -1 ? [] : base.slice(queryAt + 1).split('&').filter(Boolean);
  if (params.includes('resume=1')) return href;
  const kept = params.filter((param) => param.split('=')[0] !== 'resume');
  return `${path}?${[...kept, 'resume=1'].join('&')}${hash}`;
}

/**
 * True when `pathname` is the builder's, with or without a trailing slash or a locale prefix.
 * @param {string} pathname
 * @param {string} builderPath
 */
export function isBuilderPath(pathname, builderPath) {
  if (!builderPath) return false;
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === builderPath || trimmed.endsWith(builderPath);
}

/**
 * A selector for every builder link: the theme's, and a merchant's that points at the builder.
 * @param {string} builderPath
 */
export function builderLinks(builderPath) {
  if (!builderPath) return ENTRY;
  return `${ENTRY}, a[href*="${builderPath.replace(/["\\]/g, '\\$&')}"]`;
}

/** Collapsed whitespace, so a menu item's indentation does not hide an exact match. */
const words = (/** @type {string} */ text) => text.replace(/\s+/g, ' ').trim();

/**
 * @param {Document} doc
 * @param {{ pickup: string, builderPath: string, builderLabel: string }} config
 */
export function apply(doc, { pickup, builderPath, builderLabel }) {
  for (const link of doc.querySelectorAll(ENTRY)) {
    const label = link.querySelector(LABEL);
    if (!label) continue;
    if (!link.hasAttribute(FIXED) && label.textContent !== pickup) label.textContent = pickup;
    resume(link);
  }

  if (!builderPath) return;
  for (const link of doc.querySelectorAll(builderLinks(builderPath))) {
    if (link.matches(ENTRY) || !(link instanceof HTMLAnchorElement) || !isBuilderPath(link.pathname, builderPath)) continue;
    resume(link);
    if (builderLabel && words(link.textContent ?? '') === builderLabel) {
      const walker = doc.createTreeWalker(link, NodeFilter.SHOW_TEXT);
      /** @type {Text[]} */
      const texts = [];
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (node.nodeValue?.trim()) texts.push(/** @type {Text} */ (node));
      }
      // One run of words, as a menu item renders. Anything split across nodes is left as it is.
      if (texts.length === 1) texts[0].nodeValue = texts[0].nodeValue.replace(texts[0].nodeValue.trim(), pickup);
    }
  }
}

/** @param {Element} link */
function resume(link) {
  const href = link.getAttribute('href');
  if (href === null) return;
  const next = withResume(href);
  if (next !== href) link.setAttribute('href', next);
}

/**
 * Only records that touch a builder link are worth a pass; everything else on the page mutates too.
 * @param {MutationRecord[]} records
 * @param {string} watched a selector for every builder link
 */
export function touchesBuilderLink(records, watched) {
  for (const record of records) {
    const target = record.target.nodeType === 1 ? /** @type {Element} */ (record.target) : record.target.parentElement;
    if (target?.closest(watched)) return true;
    for (const node of record.addedNodes) {
      if (node.nodeType !== 1) continue;
      const element = /** @type {Element} */ (node);
      if (element.matches(watched) || element.querySelector(watched)) return true;
    }
  }
  return false;
}

async function boot() {
  const script = document.querySelector('script[data-club-pickup]');
  if (!(script instanceof HTMLScriptElement)) return;
  const pickup = script.dataset.clubPickup ?? '';
  if (!pickup) return;

  const store = await import('@theme/club-case-store');
  let builderPath = '';
  try {
    if (script.dataset.clubBuilder) builderPath = new URL(script.dataset.clubBuilder, location.href).pathname.replace(/\/+$/, '');
  } catch {
    builderPath = '';
  }
  const config = { pickup, builderPath, builderLabel: words(script.dataset.clubBuilderLabel ?? '') };
  const watched = builderLinks(builderPath);

  let started = false;
  const start = () => {
    if (started || !shouldPickUp(store)) return;
    started = true;
    apply(document, config);
    new MutationObserver((records) => {
      if (touchesBuilderLink(records, watched)) apply(document, config);
    }).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['href'],
    });
  };

  start();
  // Back from the funnel restores this page from the back-forward cache without running it again,
  // and that is the moment a visitor has most likely just left a quiz half answered.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) start();
  });
}

if (typeof document !== 'undefined') boot().catch(() => {});
